/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Load teacher exams and student-owned assignment snapshots through TanStack Query.
 * @state-owner TanStack Query
 * @allowed-imports same module model; app environment; contracts; TanStack Query; shared demo data
 * @forbidden routes; app bootstrap; direct generated SDK; Zustand entity copies
 * @public-api via modules/activities/index.ts
 * @side-effects authenticated GET requests outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests modules/activities/api/activities.queries.test.ts and module-contracts.test.ts
 * @security-boundary student detail reads use only authenticated student_assignment identifiers
 * @data-classification assessment content and student submission status
 * @trust-model backend verifies assignment ownership and returns answer-free public snapshots
 */

import { useQuery } from '@tanstack/react-query';
import { examRowSchema, type Role } from '@vispo/contracts';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { demoActivities } from '../../../shared/lib/demo-adapters';
import type {
  ActivityCopyTargets,
  ActivityEditorItems,
  ActivityQuestion,
  ActivitySummary,
  StudentActivityChoice,
  StudentActivityDetail,
  StudentActivityQuestion,
} from '../model/types';

const uuidSchema = z.string().uuid();

const activityEditorItemsSchema = z.object({
  organization_id: uuidSchema,
  activity_id: uuidSchema,
  activity_revision: z.number().int().positive(),
  items: z.array(
    z.object({
      id: uuidSchema,
      organization_id: uuidSchema,
      activity_id: uuidSchema,
      position: z.number().int().nonnegative(),
      item_type: z.string().min(1),
      selection_mode: z.string().nullable().optional(),
      question_content: z.record(z.string(), z.unknown()),
      options: z.array(z.record(z.string(), z.unknown())),
      answer_key: z.record(z.string(), z.unknown()).nullable().optional(),
      answer_origin: z.string().nullable().optional(),
      max_points: z.union([z.number(), z.string()]),
      requires_show_work: z.boolean(),
      revision: z.number().int().positive(),
      qa_approved_revision: z.number().int().positive().nullable().optional(),
    }),
  ),
});

const activityCopyTargetsSchema = z.object({
  source_activity_item_id: uuidSchema,
  source_item_revision: z.number().int().positive(),
  items: z.array(
    z.object({
      id: uuidSchema,
      organization_id: uuidSchema,
      organization_name: z.string().min(1),
      workspace_kind: z.enum(['personal', 'institution']),
      title: z.string().min(1),
      workflow_status: z.enum(['processing', 'needs_review', 'ready']),
      revision: z.number().int().positive(),
    }),
  ),
});

const studentExamItemSchema = z
  .object({
    assignment_id: z.string().min(1),
    exam_id: z.string().nullable().optional(),
    exam_title: z.string().min(1),
    class_name: z.string().optional().default(''),
    class_id: z.string().nullable().optional(),
    due_date: z.string().nullable().optional(),
    status: z.string(),
    percentage: z.number().nullable().optional(),
    submitted_at: z.string().nullable().optional(),
    started_at: z.string().nullable().optional(),
  })
  .loose();

const studentExamDetailSchema = z
  .object({
    assignment_id: z.string().nullable().optional(),
    student_assignment_id: z.string().nullable().optional(),
    exam_id: z.string().min(1),
    exam_title: z.string().optional().default('未命名活動'),
    class_name: z.string().optional().default(''),
    class_id: z.string().optional().default(''),
    pdf_url: z.string().nullable().optional(),
    exam_layout: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .loose();

const demoStudentQuestions: StudentActivityQuestion[] = [
  {
    id: 'student-q1',
    answerSlotId: 'student-q1',
    prompt: '解方程式 2x − 3 = 13，x = ?',
    choices: choiceList(['7', '8', '9', '12']),
    correctAnswer: '8',
    hint: '先讓等號左邊只剩 2x。−3 移到右邊時，符號會怎麼變？',
    step: '2x = 13 + 3，再把等號兩邊同除以 2。',
  },
  {
    id: 'student-q2',
    answerSlotId: 'student-q2',
    prompt: '若 4x + 5 = 21，則 x = ?',
    choices: choiceList(['3', '4', '5', '6']),
    correctAnswer: '4',
    hint: '先把左邊的 +5 處理掉，再想 4 乘以多少是 16。',
    step: '4x = 21 − 5 = 16，所以 x = 4。',
  },
  {
    id: 'student-q3',
    answerSlotId: 'student-q3',
    prompt: '3(x − 2) = 12，求 x。',
    choices: choiceList(['4', '5', '6', '8']),
    correctAnswer: '6',
    hint: '可以先把等號兩邊同除以 3，再處理括號裡的 −2。',
    step: 'x − 2 = 4，所以 x = 6。',
  },
  {
    id: 'student-q4',
    answerSlotId: 'student-q4',
    prompt: '7x = 2x + 25，求 x。',
    choices: choiceList(['4', '5', '6', '7']),
    correctAnswer: '5',
    hint: '把 2x 移到左邊，先合併同類項。',
    step: '7x − 2x = 25，因此 5x = 25，x = 5。',
  },
];

export const activityKeys = {
  all: (contextId: string, role: Role = 'teacher') => ['activities', contextId, role] as const,
  editorItems: (activityId: string) => ['activities', 'editor-items', activityId] as const,
  copyTargets: (activityItemId: string) => ['activities', 'copy-targets', activityItemId] as const,
  studentDetail: (assignmentId: string) => ['activities', 'student-detail', assignmentId] as const,
};

export function parseActivityEditorItems(payload: unknown): ActivityEditorItems {
  const result = activityEditorItemsSchema.parse(payload);
  return {
    organizationId: result.organization_id,
    activityId: result.activity_id,
    activityRevision: result.activity_revision,
    items: result.items.map((item) => ({
      id: item.id,
      organizationId: item.organization_id,
      activityId: item.activity_id,
      position: item.position,
      itemType: item.item_type,
      selectionMode: item.selection_mode ?? undefined,
      questionContent: item.question_content,
      options: item.options,
      answerKey: item.answer_key ?? undefined,
      answerOrigin: item.answer_origin ?? undefined,
      maxPoints: Number(item.max_points),
      requiresShowWork: item.requires_show_work,
      revision: item.revision,
      qaApprovedRevision: item.qa_approved_revision ?? undefined,
    })),
  };
}

export function parseActivityCopyTargets(payload: unknown): ActivityCopyTargets {
  const result = activityCopyTargetsSchema.parse(payload);
  return {
    sourceActivityItemId: result.source_activity_item_id,
    sourceItemRevision: result.source_item_revision,
    items: result.items.map((item) => ({
      id: item.id,
      organizationId: item.organization_id,
      organizationName: item.organization_name,
      workspaceKind: item.workspace_kind,
      title: item.title,
      workflowStatus: item.workflow_status,
      revision: item.revision,
    })),
  };
}

export function useActivityEditorItems(activityId: string) {
  const environment = useWorkspaceEnvironment();
  const enabled = !environment.demoMode && uuidSchema.safeParse(activityId).success;
  return useQuery({
    queryKey: activityKeys.editorItems(activityId),
    enabled,
    queryFn: async () =>
      parseActivityEditorItems(
        await environment.apiClient.request<unknown>(
          `/api/activities/${encodeURIComponent(activityId)}/items`,
        ),
      ),
  });
}

export function useActivityCopyTargets(activityItemId: string | null) {
  const environment = useWorkspaceEnvironment();
  const enabled =
    !environment.demoMode &&
    Boolean(activityItemId && uuidSchema.safeParse(activityItemId).success);
  return useQuery({
    queryKey: activityKeys.copyTargets(activityItemId ?? ''),
    enabled,
    queryFn: async () => {
      if (!activityItemId) throw new Error('Missing source activity item');
      return parseActivityCopyTargets(
        await environment.apiClient.request<unknown>(
          `/api/activity-items/${encodeURIComponent(activityItemId)}/copy-targets`,
        ),
      );
    },
  });
}

export function useActivities(activeContextId: string, role: Role = 'teacher') {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: activityKeys.all(activeContextId, role),
    queryFn: async () => {
      if (environment.demoMode) {
        return demoActivities.map(
          (activity: (typeof demoActivities)[number]): ActivitySummary =>
            role === 'student'
              ? {
                  ...activity,
                  assignmentId: `demo-assignment-${activity.id}`,
                  className: '週三數學 A 班',
                  examId: activity.id,
                  submissionStatus: activity.id === 'activity-ratio' ? 'submitted' : 'in_progress',
                  scoreLabel: activity.id === 'activity-ratio' ? '8 / 10' : undefined,
                }
              : activity,
        );
      }

      if (role === 'student') {
        const rows = z
          .array(studentExamItemSchema)
          .parse(await environment.apiClient.request<unknown>('/api/students/me/runs'));
        return rows.map(
          (row): ActivitySummary => ({
            id: row.assignment_id,
            assignmentId: row.assignment_id,
            examId: row.exam_id ?? undefined,
            title: row.exam_title.trim() || '未命名活動',
            status: 'ready',
            questions: 0,
            needsReview: 0,
            updatedLabel: studentActivityLabel(row),
            classIds: row.class_id ? [row.class_id] : [],
            className: row.class_name,
            submissionStatus: row.status,
            scoreLabel:
              typeof row.percentage === 'number' ? `${Math.round(row.percentage)}%` : undefined,
          }),
        );
      }

      const rows = z
        .array(examRowSchema)
        .parse(await environment.apiClient.request<unknown>('/api/activities'));
      return rows.flatMap((row): ActivitySummary[] => {
        if (!row.id) return [];
        const editorQuestions = extractTeacherQuestions(
          objectValue(row.exam_layout),
          row.answer_key ?? {},
        );
        const answerCount =
          editorQuestions.length || (row.answer_key ? Object.keys(row.answer_key).length : 0);
        const needsReview = editorQuestions.filter((question) => !question.reviewed).length;
        return [
          {
            id: row.id,
            title: row.title?.trim() || '未命名活動',
            status: row.status === 'published' && needsReview === 0 ? 'ready' : 'review',
            questions: answerCount,
            needsReview:
              needsReview || (row.status === 'published' ? 0 : Math.min(answerCount || 1, 3)),
            updatedLabel: row.updated_at ? relativeDateLabel(row.updated_at) : '最近',
            classIds: [],
            editorQuestions,
            questionSourceUrl: row.pdf_url ?? undefined,
            answerSourceUrl: row.answer_pdf_url ?? undefined,
          },
        ];
      });
    },
  });
}

export function useStudentRun(activity: ActivitySummary | null, role: Role) {
  const environment = useWorkspaceEnvironment();
  const assignmentId = activity?.assignmentId ?? activity?.id ?? '';
  return useQuery({
    queryKey: activityKeys.studentDetail(assignmentId),
    enabled: role === 'student' && Boolean(activity),
    queryFn: async (): Promise<StudentActivityDetail> => {
      if (!activity) throw new Error('找不到這份活動');
      if (environment.demoMode) {
        return {
          assignmentId,
          classId: activity.classIds[0] ?? 'class-8a',
          className: activity.className ?? '週三數學 A 班',
          examId: activity.examId ?? activity.id,
          questions: demoStudentQuestions,
          title: activity.title,
        };
      }

      const detail = studentExamDetailSchema.parse(
        await environment.apiClient.request<unknown>(
          `/api/students/me/runs/${encodeURIComponent(assignmentId)}`,
        ),
      );
      const questions = extractStudentQuestions(detail.exam_layout);
      return {
        assignmentId: detail.student_assignment_id ?? detail.assignment_id ?? assignmentId,
        classId: detail.class_id,
        className: detail.class_name,
        examId: detail.exam_id,
        pdfUrl: detail.pdf_url ?? undefined,
        questions,
        title: detail.exam_title.trim() || activity.title,
      };
    },
  });
}

function studentActivityLabel(row: z.infer<typeof studentExamItemSchema>): string {
  if (row.status === 'submitted' || row.status === 'graded') {
    return row.submitted_at ? `已完成 · ${relativeDateLabel(row.submitted_at)}` : '已完成';
  }
  if (row.due_date) {
    const due = new Date(row.due_date);
    if (Number.isFinite(due.getTime())) {
      return `期限 ${new Intl.DateTimeFormat('zh-TW', {
        month: 'numeric',
        day: 'numeric',
      }).format(due)}`;
    }
  }
  return row.started_at ? '繼續作答' : '待開始';
}

function choiceList(values: string[]): StudentActivityChoice[] {
  return values.map((value, index) => ({
    id: value,
    label: String.fromCharCode(65 + index),
    text: value,
  }));
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringValue(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

function collectLayoutBlocks(value: unknown, target: Record<string, unknown>[]): void {
  if (Array.isArray(value)) {
    for (const item of value) collectLayoutBlocks(item, target);
    return;
  }
  const record = objectValue(value);
  if (!record) return;
  if (typeof record.type === 'string' && objectValue(record.content)) target.push(record);
  for (const key of ['pages', 'rows', 'columns', 'blocks', 'children']) {
    if (key in record) collectLayoutBlocks(record[key], target);
  }
}

function answerSlot(
  content: Record<string, unknown>,
  block?: Record<string, unknown>,
): string | undefined {
  return stringValue(
    content.answer_slot_id,
    content.answerSlotId,
    content.snapshot_answer_slot_id,
    content.snapshotAnswerSlotId,
    block?.answer_slot_id,
    block?.answerSlotId,
  );
}

function normalizeChoices(value: unknown): StudentActivityChoice[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw, index): StudentActivityChoice[] => {
    if (typeof raw === 'string' && raw.trim()) {
      const text = raw.trim();
      return [{ id: text, label: String.fromCharCode(65 + index), text }];
    }
    const item = objectValue(raw);
    if (!item) return [];
    const id = stringValue(item.id, item.value, item.label) ?? String.fromCharCode(65 + index);
    const label = stringValue(item.label, item.display_label, item.id) ?? id;
    const text =
      stringValue(item.text, item.displayMarkdown, item.display_markdown, item.value) ?? label;
    return [{ id, label, text }];
  });
}

function choicesFromAnswerBox(content: Record<string, unknown>): StudentActivityChoice[] {
  const direct = normalizeChoices(content.choices ?? content.options);
  if (direct.length > 0) return direct;
  const children = Array.isArray(content.children) ? content.children : [];
  for (const rawChild of children) {
    const child = objectValue(rawChild);
    const childContent = objectValue(child?.content);
    if (child?.type === 'choice' && childContent) {
      const choices = normalizeChoices(childContent.choices ?? childContent.options);
      if (choices.length > 0) return choices;
    }
  }
  return [];
}

export function extractStudentQuestions(
  layout: Record<string, unknown> | null | undefined,
): StudentActivityQuestion[] {
  if (!layout) return [];
  const blocks: Record<string, unknown>[] = [];
  collectLayoutBlocks(layout, blocks);
  const questions: StudentActivityQuestion[] = [];

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    if (block?.type !== 'question') continue;
    const content = objectValue(block.content);
    if (!content) continue;
    const prompt = stringValue(
      content.text,
      content.displayMarkdown,
      content.display_markdown,
      content.prompt,
      content.label,
    );
    if (!prompt) continue;

    let slot = answerSlot(content, block);
    let choices = normalizeChoices(content.choices ?? content.options);
    for (let cursor = index + 1; cursor < blocks.length; cursor += 1) {
      const candidate = blocks[cursor];
      if (candidate?.type === 'question') break;
      if (candidate?.type !== 'answer_box') continue;
      const answerContent = objectValue(candidate.content);
      if (!answerContent) continue;
      slot ??= answerSlot(answerContent, candidate);
      if (choices.length === 0) choices = choicesFromAnswerBox(answerContent);
      break;
    }

    const id = stringValue(block.id) ?? `question-${questions.length + 1}`;
    questions.push({
      id,
      answerSlotId: slot ?? id,
      prompt,
      choices,
    });
  }
  return questions;
}

export function extractTeacherQuestions(
  layout: Record<string, unknown> | null | undefined,
  answerKey: Record<string, unknown>,
): ActivityQuestion[] {
  const publicQuestions = extractStudentQuestions(layout);
  const questions: ActivityQuestion[] = publicQuestions.map((question) => {
    const entry = objectValue(answerKey[question.answerSlotId]) ?? {};
    const guide = objectValue(entry.teaching_guide ?? entry.teachingGuide);
    const answer =
      stringValue(entry.correct_answer, entry.correctAnswer, entry.answer, entry.expected_answer) ??
      '';
    const needsReview =
      entry.needs_answer_review === true ||
      entry.needsAnswerReview === true ||
      entry.needs_question_review === true ||
      entry.needsQuestionReview === true;
    return {
      id: question.answerSlotId,
      prompt: question.prompt,
      answer,
      answerMetadata: entry,
      choices: question.choices,
      concept:
        stringValue(
          entry.concept,
          entry.concept_tag,
          entry.conceptTag,
          guide?.concept,
          guide?.topic,
        ) ?? '待確認概念',
      reviewed: Boolean(answer) && !needsReview,
    };
  });

  const covered = new Set(publicQuestions.map((question) => question.answerSlotId));
  for (const [answerSlotId, rawEntry] of Object.entries(answerKey)) {
    if (covered.has(answerSlotId)) continue;
    const entry = objectValue(rawEntry) ?? {};
    const answer =
      stringValue(entry.correct_answer, entry.correctAnswer, entry.answer, entry.expected_answer) ??
      '';
    questions.push({
      id: answerSlotId,
      prompt:
        stringValue(entry.prompt, entry.question, entry.question_text, entry.text) ??
        `第 ${questions.length + 1} 題`,
      answer,
      answerMetadata: entry,
      concept: stringValue(entry.concept, entry.concept_tag, entry.conceptTag) ?? '待確認概念',
      reviewed:
        Boolean(answer) &&
        entry.needs_answer_review !== true &&
        entry.needs_question_review !== true,
    });
  }
  return questions;
}

function relativeDateLabel(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return '最近';
  const days = Math.max(0, Math.floor((Date.now() - timestamp) / (24 * 60 * 60 * 1_000)));
  if (days === 0) return '今天';
  if (days === 1) return '昨天';
  if (days < 7) return `${days} 天前`;
  return '上週';
}
